// src/contexts/FacilityContext.tsx
"use client";

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { facilityRepository, Facility } from "@/repository/facilityRepository";
import { localAuthRepository } from "@/repository/localAuthRepository";
import { MEMBER_MANAGEMENT_MODE_CHANGED_EVENT } from "@/lib/memberManagementMode";
import { FACILITIES_UPDATED_EVENT } from "@/lib/facilityCache";
import { toast } from "@/hooks/use-toast";
import {
  FACILITY_SCOPE_QUERY_PARAM,
  getFacilityScopeFromPath,
} from "@/lib/facilityModeAccess";

export { FACILITY_SCOPE_QUERY_PARAM } from "@/lib/facilityModeAccess";

type FacilityContextType = {
  selectedFacilityId: string; // 'all' または UUID
  selectedFacilityName: string;
  facilities: Facility[];
  isLoading: boolean;
  selectFacility: (id: string) => void;
  isAllFacilities: boolean;
  canUseAllFacilities: boolean;
};

const FacilityContext = createContext<FacilityContextType | undefined>(
  undefined
);

const SELECTED_FACILITY_STORAGE_KEY = "selectedFacilityId";

// 定数: 組織全体選択時のIDと表示名
export const ALL_FACILITIES_ID = "all";
export const ALL_FACILITIES_NAME = "組織全体";

function isFacilitySelectionAvailable(
  id: string | null,
  availableFacilities: Facility[],
  canUseAllFacilities: boolean
) {
  return id === ALL_FACILITIES_ID
    ? canUseAllFacilities
    : Boolean(id && availableFacilities.some((facility) => facility.id === id));
}

export function FacilityProvider({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const locationRef = useRef(location);
  locationRef.current = location;

  const [selectedFacilityId, setSelectedFacilityId] =
    useState<string>(ALL_FACILITIES_ID);
  const [facilities, setFacilities] = useState<Facility[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [settingsRevision, setSettingsRevision] = useState(0);
  useEffect(() => {
    const reload = () => setSettingsRevision((value) => value + 1);
    window.addEventListener(MEMBER_MANAGEMENT_MODE_CHANGED_EVENT, reload);
    return () =>
      window.removeEventListener(MEMBER_MANAGEMENT_MODE_CHANGED_EVENT, reload);
  }, []);
  const canUseAllFacilities =
    localAuthRepository.getUserInfo()?.facilityScope !== "FACILITY_SPECIFIC";

  const isAvailableFacility = useCallback(
    (id: string | null) =>
      isFacilitySelectionAvailable(id, facilities, canUseAllFacilities),
    [canUseAllFacilities, facilities]
  );

  const syncFacilityToUrl = useCallback(
    (id: string, replace: boolean, clearQueryKeys: readonly string[] = []) => {
      const currentLocation = locationRef.current;
      const nextSearchParams = new URLSearchParams(currentLocation.search);
      clearQueryKeys.forEach((key) => nextSearchParams.delete(key));

      if (id) {
        nextSearchParams.set(FACILITY_SCOPE_QUERY_PARAM, id);
      } else {
        nextSearchParams.delete(FACILITY_SCOPE_QUERY_PARAM);
      }

      const nextSearch = nextSearchParams.toString();
      const currentSearch = currentLocation.search.replace(/^\?/, "");
      if (nextSearch === currentSearch) return;

      navigate(
        {
          pathname: currentLocation.pathname,
          search: nextSearch ? `?${nextSearch}` : "",
          hash: currentLocation.hash,
        },
        { replace, state: currentLocation.state }
      );
    },
    [navigate]
  );

  // 初期化：URLを優先し、LocalStorageはURLに指定がない場合の補助として使う。
  useEffect(() => {
    let sequence = 0;
    const fetchFacilities = async (initial = false) => {
      const request = ++sequence;
      const stored =
        typeof window !== "undefined"
          ? localStorage.getItem(SELECTED_FACILITY_STORAGE_KEY)
          : null;

      try {
        const res = await facilityRepository.findAll();
        if (request !== sequence) return;
        const availableFacilities =
          res.data && res.data.length > 0 ? res.data : [];
        const requestedFacilityId = new URLSearchParams(
          locationRef.current.search
        ).get(FACILITY_SCOPE_QUERY_PARAM);
        const pathFacilityId = getFacilityScopeFromPath(
          locationRef.current.pathname
        );
        const requestedIsAvailable = isFacilitySelectionAvailable(
          requestedFacilityId,
          availableFacilities,
          canUseAllFacilities
        );
        const storedIsAvailable = isFacilitySelectionAvailable(
          stored,
          availableFacilities,
          canUseAllFacilities
        );
        const pathFacilityIsAvailable = isFacilitySelectionAvailable(
          pathFacilityId,
          availableFacilities,
          canUseAllFacilities
        );
        const nextFacilityId = requestedIsAvailable
          ? requestedFacilityId!
          : pathFacilityIsAvailable
            ? pathFacilityId!
            : storedIsAvailable
              ? stored!
              : canUseAllFacilities
                ? ALL_FACILITIES_ID
                : (availableFacilities[0]?.id ?? "");

        setSelectedFacilityId(nextFacilityId);
        setFacilities(availableFacilities);
        if (typeof window !== "undefined") {
          if (nextFacilityId) {
            localStorage.setItem(SELECTED_FACILITY_STORAGE_KEY, nextFacilityId);
          } else {
            localStorage.removeItem(SELECTED_FACILITY_STORAGE_KEY);
          }
        }
        syncFacilityToUrl(nextFacilityId, true);
        setIsLoading(false);
      } catch (error) {
        if (request !== sequence) return;
        console.error("Failed to fetch facilities", error);
        if (!initial) {
          toast({
            title: "施設の変更は保存済みですが、施設一覧を更新できませんでした",
            description: "画面を再読み込みしてください。",
            variant: "destructive",
          });
          return;
        }
        const nextFacilityId = canUseAllFacilities ? ALL_FACILITIES_ID : "";
        setSelectedFacilityId(nextFacilityId);
        setFacilities([]);
        if (nextFacilityId) {
          localStorage.setItem(SELECTED_FACILITY_STORAGE_KEY, nextFacilityId);
        } else {
          localStorage.removeItem(SELECTED_FACILITY_STORAGE_KEY);
        }
        syncFacilityToUrl(nextFacilityId, true);
        setIsLoading(false);
      }
    };

    const refresh = () => {
      void fetchFacilities();
    };
    window.addEventListener(FACILITIES_UPDATED_EVENT, refresh);
    void fetchFacilities(true);
    return () => {
      sequence += 1;
      window.removeEventListener(FACILITIES_UPDATED_EVENT, refresh);
    };
  }, [canUseAllFacilities, settingsRevision, syncFacilityToUrl]);

  // URLの戻る・進むや共有URLからの遷移を現在の表示範囲へ反映する。
  useEffect(() => {
    if (isLoading) return;

    const requestedFacilityId = new URLSearchParams(location.search).get(
      FACILITY_SCOPE_QUERY_PARAM
    );
    const pathFacilityId = getFacilityScopeFromPath(location.pathname);
    const nextFacilityId = isAvailableFacility(requestedFacilityId)
      ? requestedFacilityId
      : isAvailableFacility(pathFacilityId)
        ? pathFacilityId
        : null;

    if (!nextFacilityId) {
      syncFacilityToUrl(selectedFacilityId, true);
      return;
    }
    if (requestedFacilityId !== nextFacilityId) {
      syncFacilityToUrl(nextFacilityId, true);
    }
    if (nextFacilityId === selectedFacilityId) return;

    setSelectedFacilityId(nextFacilityId);
    localStorage.setItem(SELECTED_FACILITY_STORAGE_KEY, nextFacilityId);
  }, [
    isAvailableFacility,
    isLoading,
    location.pathname,
    location.search,
    selectedFacilityId,
    syncFacilityToUrl,
  ]);

  const selectFacility = useCallback(
    (id: string) => {
      if (!isAvailableFacility(id)) return;

      // URLを表示範囲の遷移元とし、URL監視effectからContextと保存値へ反映する。
      // ユーザー操作は履歴へ追加し、戻る・進むで表示範囲を復元できるようにする。
      const currentLocation = locationRef.current;
      const previousScope = new URLSearchParams(currentLocation.search).get(
        FACILITY_SCOPE_QUERY_PARAM
      );
      // FacilityModeGuard は範囲変更中に本文をアンマウントするため、
      // 施設に依存する受講枠条件はページ側のeffectより前に解除する。
      const clearStudentSchedule =
        currentLocation.pathname.replace(/\/$/, "") === "/customers/children" &&
        previousScope !== id;
      syncFacilityToUrl(
        id,
        false,
        clearStudentSchedule ? ["course", "session", "page"] : []
      );
    },
    [isAvailableFacility, syncFacilityToUrl]
  );

  const selectedFacilityName =
    canUseAllFacilities && selectedFacilityId === ALL_FACILITIES_ID
      ? ALL_FACILITIES_NAME
      : facilities.find((f) => f.id === selectedFacilityId)?.name ||
        "施設未選択";

  const isAllFacilities =
    canUseAllFacilities && selectedFacilityId === ALL_FACILITIES_ID;

  return (
    <FacilityContext.Provider
      value={{
        selectedFacilityId,
        selectedFacilityName,
        facilities,
        isLoading,
        selectFacility,
        isAllFacilities,
        canUseAllFacilities,
      }}
    >
      {children}
    </FacilityContext.Provider>
  );
}

export function useFacility() {
  const context = useContext(FacilityContext);
  if (context === undefined) {
    throw new Error("useFacility must be used within a FacilityProvider");
  }
  return context;
}
